import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import { ButtonSize, Text } from 'wix-ui-tpa/cssVars';
import { AccessibilityHtmlTags } from '../../../../utils/accessibility/constants';
import { isDailyAgendaWeeklyPickerLayout } from '../../../../utils/layouts';
import { useCalendarActions } from '../../Hooks/useCalendarActions';
import { useSettingsParams } from '../../Hooks/useSettingsParams';
import { AgendaSlot as AgendaSlotType } from '../../ViewModel/agendaSlotsViewModel/agendaSlotsViewModel';
import { Button } from '../Button/Button';
import { classes, st } from './AgendaSlot.st.css';
import { AgendaSlotDetailType, DataHooks } from './constants';
import { DiscountName } from './DiscountName/DiscountName';

const getSlotAriaLabelledby = (id: number) =>
  [
    DataHooks.TIME,
    DataHooks.SERVICE_NAME,
    DataHooks.LOCATION,
    DataHooks.STAFF_MEMBER,
    DataHooks.DURATION,
    DataHooks.PRICE_BEFORE_DISCOUNT,
    DataHooks.PRICE,
    DataHooks.SPOTS_LEFT,
    DataHooks.POLICY_VIOLATION,
  ]
    .map((s) => `${s}-${id}`)
    .join(' ');

type AgendaSlotDetailProps = {
  type: AgendaSlotDetailType;
  content?: string;
  id?: string;
};

const AgendaSlotDetail: React.FC<AgendaSlotDetailProps> = ({
  type,
  content,
  id,
}) =>
  content ? (
    <Text
      data-hook={DataHooks.AGENDA_SLOT_DETAIL}
      data-type={type}
      tagName={AccessibilityHtmlTags.Paragraph}
      className={classes.secondaryText}
      id={id}
    >
      {content}
    </Text>
  ) : null;

export interface AgendaSlotProps {
  slot: AgendaSlotType;
  date?: string;
}

export const AgendaSlot: React.FC<AgendaSlotProps> = ({ slot, date }) => {
  const settings = useSettings();
  const settingsParams = useSettingsParams();
  const { isMobile } = useEnvironment();
  const { onSlotSelected } = useCalendarActions();

  const {
    serviceName,
    time,
    timeWidthHolder,
    id,
    spotsLeft,
    duration,
    location,
    staffMember,
    price,
    priceBeforeDiscount,
    priceAfterDiscount,
    discountName,
    showCalculatedAtCheckout,
    ctaViewModel,
    policyViolation,
    serviceIndex,
  } = slot;

  const useColorPerService = settings.get(settingsParams.useColorPerService);
  const servicesColorsType = useColorPerService
    ? settings.get(settingsParams.servicesColorsType)
    : undefined;
  const serviceColorIndex = serviceIndex % 10;

  return (
    <div
      className={st(classes.root, {
        isMobile,
        withSideBorders: settings.get(settingsParams.sideBordersVisibility),
        isDailySlotsView: isDailyAgendaWeeklyPickerLayout(
          settings,
          settingsParams,
        ),
        alignment: settings.get(settingsParams.slotAlignment),
        serviceColorIndex,
        ...(servicesColorsType ? { servicesColorsType } : {}),
      })}
      data-hook={`${DataHooks.ROOT}-${id}`}
      role="group"
      aria-labelledby={getSlotAriaLabelledby(id)}
      aria-describedby={date}
      id={`${DataHooks.ROOT}-${id}`}
      data-service-color-type={servicesColorsType}
    >
      <div className={classes.container}>
        <div className={classes.col1}>
          <Text
            data-hook={DataHooks.TIME}
            className={classes.time}
            tagName={AccessibilityHtmlTags.Paragraph}
            id={`${DataHooks.TIME}-${id}`}
          >
            {time}
          </Text>

          <Text
            data-hook={DataHooks.TIME_WIDTH_HOLDER}
            className={`${classes.time} ${classes.hidden}`}
            tagName={AccessibilityHtmlTags.Paragraph}
            aria-hidden="true"
          >
            {timeWidthHolder}
          </Text>
        </div>

        <div className={classes.detailsContainer}>
          <div className={classes.details}>
            <div className={classes.col2}>
              <div className={classes.detailsCol}>
                <Text
                  data-hook={DataHooks.SERVICE_NAME}
                  tagName={AccessibilityHtmlTags.Paragraph}
                  className={classes.serviceName}
                  id={`${DataHooks.SERVICE_NAME}-${id}`}
                >
                  {serviceName}
                </Text>
                <AgendaSlotDetail
                  type={AgendaSlotDetailType.LOCATION}
                  content={location}
                  id={`${DataHooks.LOCATION}-${id}`}
                />
              </div>
              <div className={classes.detailsCol}>
                <AgendaSlotDetail
                  type={AgendaSlotDetailType.STAFF_MEMBER}
                  content={staffMember}
                  id={`${DataHooks.STAFF_MEMBER}-${id}`}
                />
                <AgendaSlotDetail
                  type={AgendaSlotDetailType.DURATION}
                  content={duration}
                  id={`${DataHooks.DURATION}-${id}`}
                />
                <div className={classes.priceContainer}>
                  {priceBeforeDiscount && priceAfterDiscount ? (
                    <div className={classes.pricesRow}>
                      <Text
                        data-hook={DataHooks.PRICE_BEFORE_DISCOUNT}
                        className={classes.priceBeforeDiscount}
                        tagName={AccessibilityHtmlTags.Paragraph}
                        id={`${DataHooks.PRICE_BEFORE_DISCOUNT}-${id}`}
                      >
                        {priceBeforeDiscount}
                      </Text>
                      <Text
                        data-hook={DataHooks.PRICE}
                        className={classes.secondaryText}
                        tagName={AccessibilityHtmlTags.Paragraph}
                        id={`${DataHooks.PRICE}-${id}`}
                      >
                        {priceAfterDiscount}
                      </Text>
                    </div>
                  ) : (
                    <AgendaSlotDetail
                      type={AgendaSlotDetailType.PRICE}
                      content={price}
                      id={`${DataHooks.PRICE}-${id}`}
                    />
                  )}
                  <DiscountName
                    discountName={discountName}
                    showCalculatedAtCheckout={showCalculatedAtCheckout}
                  />
                </div>
              </div>
            </div>

            <div className={classes.col3}>
              <AgendaSlotDetail
                type={AgendaSlotDetailType.SPOTS_LEFT}
                content={spotsLeft}
                id={`${DataHooks.SPOTS_LEFT}-${id}`}
              />

              {policyViolation ? (
                <Text
                  data-hook={DataHooks.POLICY_VIOLATION}
                  tagName={AccessibilityHtmlTags.Paragraph}
                  className={classes.policyViolation}
                  id={`${DataHooks.POLICY_VIOLATION}-${id}`}
                >
                  {policyViolation}
                </Text>
              ) : null}

              {ctaViewModel ? (
                <Button
                  data-hook={DataHooks.CTA}
                  disabled={ctaViewModel.disabled}
                  fullWidth={ctaViewModel.fullWidth}
                  loading={ctaViewModel.loading}
                  onClick={() => onSlotSelected([id])}
                  size={ButtonSize.small}
                  wrapContent
                >
                  {ctaViewModel.label}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
